/**
 * Advanced Input nodes — based on @engine/input API.
 *
 * InputManager methods:
 * - getAction(path) → InputAction
 * - isKeyPressed(code), wasKeyPressed(code)
 * - isMouseButtonPressed(button), mousePosition, mouseDelta
 * - gamepads, getGamepad(index), touchscreen
 * - requestPointerLock, exitPointerLock, isPointerLocked
 * - triggerHaptic(index, intensity, duration)
 * - setActiveScheme(name), activeScheme
 * - enableActionMap(name), disableActionMap(name)
 *
 * InputAction properties:
 * - readValue<T>(), valueFloat, vector2
 * - isPressed, wasPressed, wasReleased, phase
 * - enabled
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// INPUT ACTIONS
// ============================================================================

/**
 * Get action by path (e.g., "Gameplay/Move")
 */
export const GetAction: NodeBehaviour = {
  id: 'input.getAction',
  name: 'Get Action',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Crosshair',
  description: 'Gets an input action by path (e.g., "Gameplay/Jump").',
  keywords: ['action', 'binding', 'input'],
  inputs: [input('path', 'Action Path', 'string', 'Gameplay/Move')],
  outputs: [output('action', 'Action', 'any')],
  compile(ctx) {
    return ctx.emit('inputManager.getAction', [ctx.resolveInput('path')], 'any');
  },
  execute(ctx) {
    const path = ctx.getInput<string>('path');
    ctx.setOutput('action', (ctx.bridge.call('inputManager.getAction', path) ?? null) as null);
  },
};

/**
 * Read action value as float
 */
export const GetActionValueFloat: NodeBehaviour = {
  id: 'input.getActionValueFloat',
  name: 'Get Action Value (Float)',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Crosshair',
  description: 'Gets the numeric value of an input action.',
  keywords: ['action', 'value', 'axis'],
  inputs: [input('path', 'Action Path', 'string', 'Gameplay/Throttle')],
  outputs: [output('value', 'Value', 'f32')],
  compile(ctx) {
    return ctx.emit('bridge:getActionValueFloat', [ctx.resolveInput('path')], 'f32');
  },
  execute(ctx) {
    const path = ctx.getInput<string>('path');
    ctx.setOutput('value', ctx.bridge.call('getActionValueFloat', path) as number ?? 0);
  },
};

/**
 * Read action value as vec2
 */
export const GetActionValueVec2: NodeBehaviour = {
  id: 'input.getActionValueVec2',
  name: 'Get Action Value (Vec2)',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Move',
  description: 'Gets the 2D vector value of an input action (e.g., Move).',
  keywords: ['action', 'vector', 'stick', 'wasd'],
  inputs: [input('path', 'Action Path', 'string', 'Gameplay/Move')],
  outputs: [
    output('vec2', 'Value', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getActionValueVec2', [ctx.resolveInput('path')], 'vec2');
  },
  execute(ctx) {
    const path = ctx.getInput<string>('path');
    const v = ctx.bridge.call('getActionValueVec2', path) as { x: number; y: number } ?? { x: 0, y: 0 };
    ctx.setOutput('vec2', v);
    ctx.setOutput('x', v.x);
    ctx.setOutput('y', v.y);
  },
};

/**
 * Check if action was pressed this frame
 */
export const WasActionPressed: NodeBehaviour = {
  id: 'input.wasActionPressed',
  name: 'Was Action Pressed',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Crosshair',
  description: 'Returns true if the action was just pressed this frame.',
  keywords: ['action', 'pressed', 'started', 'trigger'],
  inputs: [input('path', 'Action Path', 'string', 'Gameplay/Jump')],
  outputs: [output('pressed', 'Was Pressed', 'bool')],
  compile(ctx) {
    return ctx.emit('bridge:wasActionPressed', [ctx.resolveInput('path')], 'bool');
  },
  execute(ctx) {
    const path = ctx.getInput<string>('path');
    ctx.setOutput('pressed', ctx.bridge.call('wasActionPressed', path) as boolean ?? false);
  },
};

/**
 * Check if action was released this frame
 */
export const WasActionReleased: NodeBehaviour = {
  id: 'input.wasActionReleased',
  name: 'Was Action Released',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Crosshair',
  description: 'Returns true if the action was just released this frame.',
  keywords: ['action', 'released', 'ended'],
  inputs: [input('path', 'Action Path', 'string', 'Gameplay/Jump')],
  outputs: [output('released', 'Was Released', 'bool')],
  compile(ctx) {
    return ctx.emit('bridge:wasActionReleased', [ctx.resolveInput('path')], 'bool');
  },
  execute(ctx) {
    const path = ctx.getInput<string>('path');
    ctx.setOutput('released', ctx.bridge.call('wasActionReleased', path) as boolean ?? false);
  },
};

/**
 * Check if action is currently pressed
 */
export const IsActionPressed: NodeBehaviour = {
  id: 'input.isActionPressed',
  name: 'Is Action Pressed',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Crosshair',
  description: 'Returns true while the action is being held.',
  keywords: ['action', 'held', 'performing'],
  inputs: [input('path', 'Action Path', 'string', 'Gameplay/Fire')],
  outputs: [output('pressed', 'Is Pressed', 'bool')],
  compile(ctx) {
    return ctx.emit('bridge:isActionPressed', [ctx.resolveInput('path')], 'bool');
  },
  execute(ctx) {
    const path = ctx.getInput<string>('path');
    ctx.setOutput('pressed', ctx.bridge.call('isActionPressed', path) as boolean ?? false);
  },
};

/**
 * Get action phase
 */
export const GetActionPhase: NodeBehaviour = {
  id: 'input.getActionPhase',
  name: 'Get Action Phase',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Crosshair',
  description: 'Gets the current phase of an action (waiting, started, performed, canceled).',
  keywords: ['action', 'phase', 'state'],
  inputs: [input('path', 'Action Path', 'string', 'Gameplay/Jump')],
  outputs: [output('phase', 'Phase', 'string')],
  compile(ctx) {
    return ctx.emit('bridge:getActionPhase', [ctx.resolveInput('path')], 'string');
  },
  execute(ctx) {
    const path = ctx.getInput<string>('path');
    ctx.setOutput('phase', ctx.bridge.call('getActionPhase', path) as string ?? 'waiting');
  },
};

/**
 * Enable/disable an action
 */
export const SetActionEnabled: NodeBehaviour = {
  id: 'input.setActionEnabled',
  name: 'Set Action Enabled',
  category: 'Input',
  color: '#e67e22',
  icon: 'Crosshair',
  description: 'Enables or disables an input action.',
  keywords: ['action', 'enable', 'disable'],
  inputs: [
    flowIn(),
    input('path', 'Action Path', 'string', 'Gameplay/Jump'),
    input('enabled', 'Enabled', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setActionEnabled', [ctx.resolveInput('path'), ctx.resolveInput('enabled')], 'void', true);
  },
  execute(ctx) {
    const path = ctx.getInput<string>('path');
    const enabled = ctx.getInput<boolean>('enabled');
    ctx.bridge.call('setActionEnabled', path, enabled);
  },
};

// ============================================================================
// ACTION MAPS
// ============================================================================

/**
 * Enable an action map
 */
export const EnableActionMap: NodeBehaviour = {
  id: 'input.enableActionMap',
  name: 'Enable Action Map',
  category: 'Input',
  color: '#e67e22',
  icon: 'Layers',
  description: 'Enables an action map by name.',
  keywords: ['action', 'map', 'enable'],
  inputs: [flowIn(), input('name', 'Map Name', 'string', 'Gameplay')],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('inputManager.enableActionMap', [ctx.resolveInput('name')], 'void', true);
  },
  execute(ctx) {
    const name = ctx.getInput<string>('name');
    ctx.bridge.call('inputManager.enableActionMap', name);
  },
};

/**
 * Disable an action map
 */
export const DisableActionMap: NodeBehaviour = {
  id: 'input.disableActionMap',
  name: 'Disable Action Map',
  category: 'Input',
  color: '#e67e22',
  icon: 'Layers',
  description: 'Disables an action map by name.',
  keywords: ['action', 'map', 'disable'],
  inputs: [flowIn(), input('name', 'Map Name', 'string', 'Gameplay')],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('inputManager.disableActionMap', [ctx.resolveInput('name')], 'void', true);
  },
  execute(ctx) {
    const name = ctx.getInput<string>('name');
    ctx.bridge.call('inputManager.disableActionMap', name);
  },
};

// ============================================================================
// CONTROL SCHEMES
// ============================================================================

/**
 * Get active control scheme
 */
export const GetActiveControlScheme: NodeBehaviour = {
  id: 'input.getActiveScheme',
  name: 'Get Active Control Scheme',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Settings',
  description: 'Gets the currently active control scheme name.',
  keywords: ['scheme', 'controller', 'keyboard'],
  inputs: [],
  outputs: [output('scheme', 'Scheme Name', 'string')],
  compile(ctx) {
    return ctx.emit('bridge:getActiveControlScheme', [], 'string');
  },
  execute(ctx) {
    ctx.setOutput('scheme', ctx.bridge.call('getActiveControlScheme') as string ?? 'keyboard');
  },
};

/**
 * Set active control scheme
 */
export const SetActiveControlScheme: NodeBehaviour = {
  id: 'input.setActiveScheme',
  name: 'Set Active Control Scheme',
  category: 'Input',
  color: '#e67e22',
  icon: 'Settings',
  description: 'Sets the active control scheme by name.',
  keywords: ['scheme', 'controller', 'keyboard', 'switch'],
  inputs: [flowIn(), input('name', 'Scheme Name', 'string', 'Keyboard+Mouse')],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('inputManager.setActiveScheme', [ctx.resolveInput('name')], 'void', true);
  },
  execute(ctx) {
    const name = ctx.getInput<string>('name');
    ctx.bridge.call('inputManager.setActiveScheme', name);
  },
};

// ============================================================================
// KEYBOARD
// ============================================================================

/**
 * Check if key was just pressed
 */
export const WasKeyPressed: NodeBehaviour = {
  id: 'input.wasKeyPressed',
  name: 'Was Key Pressed',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Keyboard',
  description: 'Returns true if a key was just pressed this frame.',
  keywords: ['keyboard', 'key', 'down'],
  inputs: [input('code', 'Key Code', 'string', 'Space')],
  outputs: [output('pressed', 'Was Pressed', 'bool')],
  compile(ctx) {
    return ctx.emit('inputManager.wasKeyPressed', [ctx.resolveInput('code')], 'bool');
  },
  execute(ctx) {
    const code = ctx.getInput<string>('code');
    ctx.setOutput('pressed', ctx.bridge.call('inputManager.wasKeyPressed', code) as boolean ?? false);
  },
};

// ============================================================================
// MOUSE
// ============================================================================

/**
 * Get mouse delta
 */
export const GetMouseDelta: NodeBehaviour = {
  id: 'input.mouseDelta',
  name: 'Get Mouse Delta',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Mouse',
  description: 'Gets mouse movement since last frame.',
  keywords: ['mouse', 'movement', 'delta'],
  inputs: [],
  outputs: [
    output('delta', 'Delta', 'vec2'),
    output('dx', 'Delta X', 'f32'),
    output('dy', 'Delta Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('inputManager.mouseDelta', [], 'vec2');
  },
  execute(ctx) {
    const d = ctx.bridge.call('inputManager.mouseDelta') as { x: number; y: number } ?? { x: 0, y: 0 };
    ctx.setOutput('delta', d);
    ctx.setOutput('dx', d.x);
    ctx.setOutput('dy', d.y);
  },
};

/**
 * Get mouse scroll
 */
export const GetMouseScroll: NodeBehaviour = {
  id: 'input.mouseScroll',
  name: 'Get Mouse Scroll',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Mouse',
  description: 'Gets mouse scroll wheel delta this frame.',
  keywords: ['mouse', 'wheel', 'scroll', 'zoom'],
  inputs: [],
  outputs: [
    output('scroll', 'Scroll', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getMouseScroll', [], 'vec2');
  },
  execute(ctx) {
    const s = ctx.bridge.call('getMouseScroll') as { x: number; y: number } ?? { x: 0, y: 0 };
    ctx.setOutput('scroll', s);
    ctx.setOutput('x', s.x);
    ctx.setOutput('y', s.y);
  },
};

/**
 * Check if mouse button is pressed
 */
export const IsMouseButtonPressed: NodeBehaviour = {
  id: 'input.isMouseButtonPressed',
  name: 'Is Mouse Button Pressed',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Mouse',
  description: 'Returns true while a mouse button is held.',
  keywords: ['mouse', 'button', 'click'],
  inputs: [input('button', 'Button', 'string', 'leftButton')],
  outputs: [output('pressed', 'Is Pressed', 'bool')],
  compile(ctx) {
    return ctx.emit('inputManager.isMouseButtonPressed', [ctx.resolveInput('button')], 'bool');
  },
  execute(ctx) {
    const button = ctx.getInput<string>('button');
    ctx.setOutput('pressed', ctx.bridge.call('inputManager.isMouseButtonPressed', button) as boolean ?? false);
  },
};

// ============================================================================
// POINTER LOCK
// ============================================================================

/**
 * Request pointer lock (for FPS-style mouse look)
 */
export const RequestPointerLock: NodeBehaviour = {
  id: 'input.requestPointerLock',
  name: 'Request Pointer Lock',
  category: 'Input',
  color: '#e67e22',
  icon: 'Lock',
  description: 'Requests pointer lock for mouse capture.',
  keywords: ['mouse', 'lock', 'capture', 'fps'],
  inputs: [flowIn()],
  outputs: [flowOut(), output('success', 'Success', 'bool')],
  compile(ctx) {
    return ctx.emit('inputManager.requestPointerLock', [], 'bool', true);
  },
  execute(ctx) {
    const result = ctx.bridge.call('inputManager.requestPointerLock') as boolean ?? false;
    ctx.setOutput('success', result);
  },
};

/**
 * Exit pointer lock
 */
export const ExitPointerLock: NodeBehaviour = {
  id: 'input.exitPointerLock',
  name: 'Exit Pointer Lock',
  category: 'Input',
  color: '#e67e22',
  icon: 'Unlock',
  description: 'Releases pointer lock.',
  keywords: ['mouse', 'unlock', 'release'],
  inputs: [flowIn()],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('inputManager.exitPointerLock', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('inputManager.exitPointerLock');
  },
};

/**
 * Check if pointer is locked
 */
export const IsPointerLocked: NodeBehaviour = {
  id: 'input.isPointerLocked',
  name: 'Is Pointer Locked',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Lock',
  description: 'Returns true if pointer lock is active.',
  keywords: ['mouse', 'lock', 'captured'],
  inputs: [],
  outputs: [output('locked', 'Is Locked', 'bool')],
  compile(ctx) {
    return ctx.emit('inputManager.isPointerLocked', [], 'bool');
  },
  execute(ctx) {
    ctx.setOutput('locked', ctx.bridge.call('inputManager.isPointerLocked') as boolean ?? false);
  },
};

// ============================================================================
// GAMEPAD
// ============================================================================

/**
 * Get gamepad by index
 */
export const GetGamepad: NodeBehaviour = {
  id: 'input.getGamepad',
  name: 'Get Gamepad',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Gamepad2',
  description: 'Gets a gamepad device by index.',
  keywords: ['gamepad', 'controller', 'joystick'],
  inputs: [input('index', 'Index', 'i32', 0)],
  outputs: [output('gamepad', 'Gamepad', 'any'), output('connected', 'Is Connected', 'bool')],
  compile(ctx) {
    return ctx.emit('inputManager.getGamepad', [ctx.resolveInput('index')], 'any');
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const gp = ctx.bridge.call('inputManager.getGamepad', index);
    ctx.setOutput('gamepad', (gp ?? null) as null);
    ctx.setOutput('connected', gp != null);
  },
};

/**
 * Get gamepad stick value
 */
export const GetGamepadStick: NodeBehaviour = {
  id: 'input.getGamepadStick',
  name: 'Get Gamepad Stick',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Gamepad2',
  description: 'Gets the value of a gamepad stick (leftStick or rightStick).',
  keywords: ['gamepad', 'stick', 'analog', 'joystick'],
  inputs: [
    input('index', 'Gamepad Index', 'i32', 0),
    input('stick', 'Stick', 'string', 'leftStick'),
  ],
  outputs: [
    output('value', 'Value', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit(
      'bridge:getGamepadStick',
      [ctx.resolveInput('index'), ctx.resolveInput('stick')],
      'vec2',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const stick = ctx.getInput<string>('stick');
    const v = ctx.bridge.call('getGamepadStick', index, stick) as { x: number; y: number } ?? { x: 0, y: 0 };
    ctx.setOutput('value', v);
    ctx.setOutput('x', v.x);
    ctx.setOutput('y', v.y);
  },
};

/**
 * Get gamepad trigger value
 */
export const GetGamepadTrigger: NodeBehaviour = {
  id: 'input.getGamepadTrigger',
  name: 'Get Gamepad Trigger',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Gamepad2',
  description: 'Gets the analog value of a gamepad trigger (leftTriggerAxis/rightTriggerAxis).',
  keywords: ['gamepad', 'trigger', 'lt', 'rt'],
  inputs: [
    input('index', 'Gamepad Index', 'i32', 0),
    input('trigger', 'Trigger', 'string', 'leftTriggerAxis'),
  ],
  outputs: [output('value', 'Value', 'f32')],
  compile(ctx) {
    return ctx.emit(
      'bridge:getGamepadTrigger',
      [ctx.resolveInput('index'), ctx.resolveInput('trigger')],
      'f32',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const trigger = ctx.getInput<string>('trigger');
    ctx.setOutput('value', ctx.bridge.call('getGamepadTrigger', index, trigger) as number ?? 0);
  },
};

/**
 * Check gamepad button pressed
 */
export const IsGamepadButtonPressed: NodeBehaviour = {
  id: 'input.isGamepadButtonPressed',
  name: 'Is Gamepad Button Pressed',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Gamepad2',
  description: 'Returns true while a gamepad button is held (a, b, x, y, leftBumper, etc.).',
  keywords: ['gamepad', 'button', 'pressed'],
  inputs: [
    input('index', 'Gamepad Index', 'i32', 0),
    input('button', 'Button', 'string', 'a'),
  ],
  outputs: [output('pressed', 'Is Pressed', 'bool')],
  compile(ctx) {
    return ctx.emit(
      'bridge:isGamepadButtonPressed',
      [ctx.resolveInput('index'), ctx.resolveInput('button')],
      'bool',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const button = ctx.getInput<string>('button');
    ctx.setOutput('pressed', ctx.bridge.call('isGamepadButtonPressed', index, button) as boolean ?? false);
  },
};

/**
 * Check gamepad button was pressed
 */
export const WasGamepadButtonPressed: NodeBehaviour = {
  id: 'input.wasGamepadButtonPressed',
  name: 'Was Gamepad Button Pressed',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Gamepad2',
  description: 'Returns true if a gamepad button was just pressed this frame.',
  keywords: ['gamepad', 'button', 'down'],
  inputs: [
    input('index', 'Gamepad Index', 'i32', 0),
    input('button', 'Button', 'string', 'a'),
  ],
  outputs: [output('pressed', 'Was Pressed', 'bool')],
  compile(ctx) {
    return ctx.emit(
      'bridge:wasGamepadButtonPressed',
      [ctx.resolveInput('index'), ctx.resolveInput('button')],
      'bool',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const button = ctx.getInput<string>('button');
    ctx.setOutput('pressed', ctx.bridge.call('wasGamepadButtonPressed', index, button) as boolean ?? false);
  },
};

/**
 * Get D-Pad as vector
 */
export const GetGamepadDPad: NodeBehaviour = {
  id: 'input.getGamepadDPad',
  name: 'Get Gamepad D-Pad',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Gamepad2',
  description: 'Gets the D-Pad value as a vec2.',
  keywords: ['gamepad', 'dpad', 'direction'],
  inputs: [input('index', 'Gamepad Index', 'i32', 0)],
  outputs: [
    output('value', 'Value', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit(
      'bridge:getGamepadDPad',
      [ctx.resolveInput('index')],
      'vec2',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const v = ctx.bridge.call('getGamepadDPad', index) as { x: number; y: number } ?? { x: 0, y: 0 };
    ctx.setOutput('value', v);
    ctx.setOutput('x', v.x);
    ctx.setOutput('y', v.y);
  },
};

/**
 * Trigger gamepad haptic feedback
 */
export const TriggerHaptic: NodeBehaviour = {
  id: 'input.triggerHaptic',
  name: 'Trigger Haptic',
  category: 'Input',
  color: '#e67e22',
  icon: 'Vibrate',
  description: 'Triggers haptic feedback (vibration) on a gamepad.',
  keywords: ['gamepad', 'vibrate', 'rumble', 'haptic'],
  inputs: [
    flowIn(),
    input('index', 'Gamepad Index', 'i32', 0),
    input('intensity', 'Intensity', 'f32', 0.5),
    input('duration', 'Duration (s)', 'f32', 0.2),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit(
      'inputManager.triggerHaptic',
      [ctx.resolveInput('index'), ctx.resolveInput('intensity'), ctx.resolveInput('duration')],
      'void',
      true,
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const intensity = ctx.getInput<number>('intensity');
    const duration = ctx.getInput<number>('duration');
    ctx.bridge.call('inputManager.triggerHaptic', index, intensity, duration);
  },
};

// ============================================================================
// TOUCHSCREEN
// ============================================================================

/**
 * Get touch count
 */
export const GetTouchCount: NodeBehaviour = {
  id: 'input.getTouchCount',
  name: 'Get Touch Count',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Hand',
  description: 'Gets the number of active touches.',
  keywords: ['touch', 'count', 'fingers'],
  inputs: [],
  outputs: [output('count', 'Count', 'i32')],
  compile(ctx) {
    return ctx.emit('bridge:getTouchCount', [], 'i32');
  },
  execute(ctx) {
    ctx.setOutput('count', ctx.bridge.call('getTouchCount') as number ?? 0);
  },
};

/**
 * Get touch position
 */
export const GetTouchPosition: NodeBehaviour = {
  id: 'input.getTouchPosition',
  name: 'Get Touch Position',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Hand',
  description: 'Gets the position of a touch by index.',
  keywords: ['touch', 'position', 'finger'],
  inputs: [input('index', 'Touch Index', 'i32', 0)],
  outputs: [
    output('pos', 'Position', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
    output('active', 'Is Active', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit(
      'bridge:getTouchPosition',
      [ctx.resolveInput('index')],
      'vec2',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const result = ctx.bridge.call('getTouchPosition', index) as { x: number; y: number; active?: boolean } ?? { x: 0, y: 0 };
    ctx.setOutput('pos', { x: result.x, y: result.y });
    ctx.setOutput('x', result.x);
    ctx.setOutput('y', result.y);
    ctx.setOutput('active', result.active ?? false);
  },
};

/**
 * Get touch delta (movement since last frame)
 */
export const GetTouchDelta: NodeBehaviour = {
  id: 'input.getTouchDelta',
  name: 'Get Touch Delta',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Hand',
  description: 'Gets the movement delta of a touch.',
  keywords: ['touch', 'delta', 'movement'],
  inputs: [input('index', 'Touch Index', 'i32', 0)],
  outputs: [
    output('delta', 'Delta', 'vec2'),
    output('dx', 'Delta X', 'f32'),
    output('dy', 'Delta Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit(
      'bridge:getTouchDelta',
      [ctx.resolveInput('index')],
      'vec2',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    const d = ctx.bridge.call('getTouchDelta', index) as { x: number; y: number } ?? { x: 0, y: 0 };
    ctx.setOutput('delta', d);
    ctx.setOutput('dx', d.x);
    ctx.setOutput('dy', d.y);
  },
};

/**
 * Get primary touch
 */
export const GetPrimaryTouch: NodeBehaviour = {
  id: 'input.getPrimaryTouch',
  name: 'Get Primary Touch',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Hand',
  description: 'Gets the primary (first) touch position and state.',
  keywords: ['touch', 'primary', 'first'],
  inputs: [],
  outputs: [
    output('pos', 'Position', 'vec2'),
    output('delta', 'Delta', 'vec2'),
    output('active', 'Is Active', 'bool'),
    output('pressure', 'Pressure', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getPrimaryTouch', [], 'any');
  },
  execute(ctx) {
    const t = ctx.bridge.call('getPrimaryTouch') as { x: number; y: number; phase: string } | null;
    ctx.setOutput('pos', t ? { x: t.x, y: t.y } : { x: 0, y: 0 });
    ctx.setOutput('delta', { x: 0, y: 0 });
    ctx.setOutput('active', t != null);
    ctx.setOutput('pressure', t != null ? 1 : 0);
  },
};

/**
 * Check if touch is active
 */
export const IsTouchActive: NodeBehaviour = {
  id: 'input.isTouchActive',
  name: 'Is Touch Active',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Hand',
  description: 'Returns true if a touch at the given index is active.',
  keywords: ['touch', 'active', 'finger'],
  inputs: [input('index', 'Touch Index', 'i32', 0)],
  outputs: [output('active', 'Is Active', 'bool')],
  compile(ctx) {
    return ctx.emit(
      'bridge:isTouchActive',
      [ctx.resolveInput('index')],
      'bool',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    ctx.setOutput('active', ctx.bridge.call('isTouchActive', index) as boolean ?? false);
  },
};

/**
 * Get touch phase
 */
export const GetTouchPhase: NodeBehaviour = {
  id: 'input.getTouchPhase',
  name: 'Get Touch Phase',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Hand',
  description: 'Gets the phase of a touch (began, moved, stationary, ended, canceled).',
  keywords: ['touch', 'phase', 'state'],
  inputs: [input('index', 'Touch Index', 'i32', 0)],
  outputs: [output('phase', 'Phase', 'string')],
  compile(ctx) {
    return ctx.emit(
      'bridge:getTouchPhase',
      [ctx.resolveInput('index')],
      'string',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    ctx.setOutput('phase', ctx.bridge.call('getTouchPhase', index) as string ?? 'none');
  },
};

/**
 * Get touch pressure
 */
export const GetTouchPressure: NodeBehaviour = {
  id: 'input.getTouchPressure',
  name: 'Get Touch Pressure',
  category: 'Input',
  isPure: true,
  color: '#e67e22',
  icon: 'Hand',
  description: 'Gets the pressure of a touch (0-1).',
  keywords: ['touch', 'pressure', 'force'],
  inputs: [input('index', 'Touch Index', 'i32', 0)],
  outputs: [output('pressure', 'Pressure', 'f32')],
  compile(ctx) {
    return ctx.emit(
      'bridge:getTouchPressure',
      [ctx.resolveInput('index')],
      'f32',
    );
  },
  execute(ctx) {
    const index = ctx.getInput<number>('index');
    ctx.setOutput('pressure', ctx.bridge.call('getTouchPressure', index) as number ?? 0);
  },
};

// ============================================================================
// EXPORT
// ============================================================================

export const INPUT_ADVANCED_NODES: NodeBehaviour[] = [
  // Actions
  GetAction,
  GetActionValueFloat,
  GetActionValueVec2,
  WasActionPressed,
  WasActionReleased,
  IsActionPressed,
  GetActionPhase,
  SetActionEnabled,
  // Action Maps
  EnableActionMap,
  DisableActionMap,
  // Control Schemes
  GetActiveControlScheme,
  SetActiveControlScheme,
  // Keyboard
  WasKeyPressed,
  // Mouse
  GetMouseDelta,
  GetMouseScroll,
  IsMouseButtonPressed,
  // Pointer Lock
  RequestPointerLock,
  ExitPointerLock,
  IsPointerLocked,
  // Gamepad
  GetGamepad,
  GetGamepadStick,
  GetGamepadTrigger,
  IsGamepadButtonPressed,
  WasGamepadButtonPressed,
  GetGamepadDPad,
  TriggerHaptic,
  // Touch
  GetTouchCount,
  GetTouchPosition,
  GetTouchDelta,
  GetPrimaryTouch,
  IsTouchActive,
  GetTouchPhase,
  GetTouchPressure,
];
